/**
 * time zone option services to add internationalization support to the modules.
 *
 * This interface defines getter, setter and subscription methods for managing available time zone options and the current
 * time zone option setting per application module. It provides a flexible way to manage time zone option settings and ensure that
 * the application can adapt to different time zone option preferences.
 *
 * ## Common Concepts
 *
 * The time zone option services are part of the AppFrame Services interface, and they can be accessed through the `timezone`
 * property. This interface is designed to be used in the context of an application that supports multiple time zone options
 * and allows users to switch between them dynamically.
 *
 * @module Services > Feature Services > Timezone Services
 */
import { UnsubscribeMethod } from '../../common';

/**
 * Define the values of available date and time usage.
 * LOCAL: Use the local date and time of the user's device.
 * UTC: Use Coordinated Universal Time (UTC) for date and time representation.
 * UTC_WITH_LOCAL: Display both UTC and local date and time, allowing users to see the time in both formats.
 */
export enum Timezone {
  LOCAL = 'LOCAL',
  UTC = 'UTC',
  UTC_WITH_LOCAL = 'UTC_WITH_LOCAL',
}
/**
 * Service method to check if timezone feature is currently enabled / shown.
 */
export type IsShowTimezoneService = () => boolean;

/**
 * Service method to enable or disable timezone feature.
 */
export type SetTimezoneAllowedService = (timezoneAllowed: boolean) => void;

/**
 * Callback invoked when the timezone allowed state changes.
 */
export type OnShowTimezoneChangeServiceCallback = (timezoneAllowed: boolean) => void;

/**
 * Service method to subscribe for timezone allowed state changes.
 */
export type OnTimezoneAllowedChangeService = (callback: OnShowTimezoneChangeServiceCallback) => UnsubscribeMethod;

/**
 * Retrieves the currently selected time zone option.
 *
 * The current time zone can be set with the `setTimezone` service call, and it can be retrieved with this
 * service call.
 *
 * @returns The currently selected time zone value.
 */
export type GetTimezoneService = () => Timezone;

/**
 * Sets the currently selected time zone option.
 *
 * The current time zone option can be set with this service call, and it can be retrieved with the
 * `getTimezone` service call.
 *
 * It is possible to subscribe to time zone changes with the `onTimezoneChange` service call.
 *
 * @param timezone The time zone option to set as the current time zone.
 */
export type SetTimezoneService = (timezone: Timezone) => void;

/**
 * Callback method which is invoked when the current time zone option is changed.
 *
 * It is similar to the `getTimezone` service call, but it is invoked when the time zone option is changed.
 * It can be used to update the application state or UI when the time zone option is changed.
 *
 * @param timezone The new currently selected time zone option.
 */
export type OnTimezoneChangeServiceCallback = (timezone: Timezone) => void;

/**
 * Subscribe to current time zone option changes.
 *
 * @param callback Callback method which is invoked when the current time zone option is changed.
 * @returns A method to unsubscribe from the actual time zone option change subscription.
 */
export type OnTimezoneChangeService = (callback: OnTimezoneChangeServiceCallback) => UnsubscribeMethod;

/**
 * Service collection to manage time zone options in the application.
 *
 * This service collection provides methods to get, set and subscribe to changes in the available time zone options
 * and the currently selected time zone option. It allows the application to adapt to different time zone option preferences
 * and provides a flexible way to manage time zone option settings.
 **/
export interface TimezoneServices {
  /**
   * Returns whether dark mode feature is currently turned on or off.
   *
   * Turning off dark mode will remove the dark mode selector from the Preferences menu.
   *
   * @returns {boolean} `true` if dark mode feature is on, `false` otherwise.
   */
  isTimezoneAllowed: IsShowTimezoneService;

  /**
   * Sets whether timezone feature is currently allowed or not.
   *
   * @param {boolean} timezoneAllowed `true` to allow timezone feature, `false` to disallow
   * it.
   */
  setTimezoneAllowed: SetTimezoneAllowedService;

  /**
   * Registers a callback which is invoked when the timezone allowed state changes.
   *
   * @param {OnShowTimezoneChangeServiceCallback} callback The callback to be called on
   * timezone allowed change.
   * @returns {UnsubscribeMethod} A method to unsubscribe the callback.
   */
  onTimezoneAllowedChange: OnTimezoneAllowedChangeService;

  /**
   * @inheritDoc GetTimezoneService
   */
  getTimezone: GetTimezoneService;

  /**
   * @inheritDoc SetTimezoneService
   */
  setTimezone: SetTimezoneService;

  /**
   * @inheritDoc OnTimezoneChangeService
   */
  onTimezoneChange: OnTimezoneChangeService;
}

declare module 'types/services/appframe' {
  interface AppFrameServices {
    /**
     * Service collection to manage time zone options in the application.
     *
     * This service collection provides methods to get, set and subscribe to changes in the available time zone options
     * and the currently selected time zone option. It allows the application to adapt to different time zone option preferences
     * and provides a flexible way to manage time zone option settings.
     **/
    timezone: TimezoneServices;
  }
}
